import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { toast } from 'components/ui/toast-store'
import moment from 'moment'
import {
	Check,
	Copy,
	Loader2,
	Mail,
	Trash2,
} from 'lucide-react'
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from 'components/ui/dialog'
import { Button } from 'components/ui/button'
import { Input } from 'components/ui/input'
import { Label } from 'components/ui/label'
import { Textarea } from 'components/ui/textarea'
import { Select } from 'components/ui/select'
import { useShareLink } from '../hooks/useShareLink'
import {
	getCurrentVersion,
	type CreateShareLinkResponse,
	type DocumentLibraryState,
} from '../types/documentLibrary.types'
import { getDocumentIcon } from '../utils/getDocumentIcon'

interface DocumentShareDialogProps {
	open: boolean
	onClose: () => void
	documentId: string | null
	/** When omitted, the backend defaults to the document's current version. */
	versionId?: string
}

const TTL_OPTIONS: { value: number; label: string }[] = [
	{ value: 1, label: '1 day' },
	{ value: 7, label: '7 days' },
	{ value: 30, label: '30 days' },
	{ value: 90, label: '90 days' },
]

const isProbablyEmail = (s: string): boolean => /^\S+@\S+\.\S+$/.test(s)

const DocumentShareDialog: React.FC<DocumentShareDialogProps> = ({
	open,
	onClose,
	documentId,
	versionId,
}) => {
	const { createLink, revokeLink } = useShareLink(documentId)

	const libraryDoc = useSelector(
		(state: { libraryDocuments: DocumentLibraryState }) =>
			documentId ? state.libraryDocuments.entities[documentId] : undefined,
	)
	const currentVersion = getCurrentVersion(libraryDoc)
	const headerFilename =
		currentVersion?.originalFilename || libraryDoc?.libraryName || ''
	const fileIcon = getDocumentIcon(headerFilename)

	const [link, setLink] = useState<CreateShareLinkResponse | null>(null)
	const [ttlDays, setTtlDays] = useState<number>(7)
	const [email, setEmail] = useState('')
	const [message, setMessage] = useState('')

	const [creating, setCreating] = useState(false)
	const [revoking, setRevoking] = useState(false)
	const [sending, setSending] = useState(false)
	const [copied, setCopied] = useState(false)
	const [emailSentTo, setEmailSentTo] = useState<string | null>(null)
	const [error, setError] = useState<string | null>(null)

	const previousLinkRef = useRef<CreateShareLinkResponse | null>(null)

	const issueLink = useCallback(
		async (nextTtl: number) => {
			if (!documentId) return
			setCreating(true)
			setError(null)
			try {
				const fresh = await createLink({
					documentId,
					versionId,
					ttlDays: nextTtl,
				})
				const previous = previousLinkRef.current
				setLink(fresh)
				previousLinkRef.current = fresh
				// Best-effort revoke of the previous link so it doesn't keep
				// working alongside the new one (matches "TTL change re-issues
				// the token (old token becomes inert)" acceptance).
				if (previous?.token && previous.token !== fresh.token) {
					revokeLink({ token: previous.token, documentId }).catch(() => {})
				}
			} catch (e: any) {
				setError(e?.message || 'Could not create share link')
			} finally {
				setCreating(false)
			}
		},
		[documentId, versionId, createLink, revokeLink],
	)

	// Auto-create on open. Cleanup on close resets state.
	useEffect(() => {
		if (open && documentId && !link && !creating) {
			issueLink(ttlDays)
		}
		if (!open) {
			setLink(null)
			previousLinkRef.current = null
			setTtlDays(7)
			setEmail('')
			setMessage('')
			setError(null)
			setCopied(false)
			setEmailSentTo(null)
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [open, documentId])

	const handleTtlChange = useCallback(
		(e: React.ChangeEvent<HTMLSelectElement>) => {
			const next = Number(e.target.value)
			if (!Number.isFinite(next) || next === ttlDays) return
			setTtlDays(next)
			issueLink(next)
		},
		[ttlDays, issueLink],
	)

	const handleCopy = useCallback(async () => {
		if (!link?.url) return
		try {
			if (navigator?.clipboard?.writeText) {
				await navigator.clipboard.writeText(link.url)
			} else {
				// Older-browser fallback. document.execCommand('copy') still
				// works in jsdom-style environments and on Safari < 13.4.
				const textarea = document.createElement('textarea')
				textarea.value = link.url
				textarea.setAttribute('readonly', '')
				textarea.style.position = 'absolute'
				textarea.style.left = '-9999px'
				document.body.appendChild(textarea)
				textarea.select()
				document.execCommand('copy')
				document.body.removeChild(textarea)
			}
			setCopied(true)
			setTimeout(() => setCopied(false), 2000)
		} catch {
			setError('Could not copy to clipboard')
		}
	}, [link])

	const handleSendEmail = useCallback(async () => {
		const recipient = email.trim()
		if (!documentId || !recipient || !isProbablyEmail(recipient)) return
		setSending(true)
		setError(null)
		try {
			// Backend creates a new token + dispatches the email. The
			// existing copy-link is unaffected — recipient gets their own
			// token, sender keeps theirs.
			await createLink({
				documentId,
				versionId,
				ttlDays,
				email: recipient,
				message: message.trim() || undefined,
			})
			setEmailSentTo(recipient)
			setEmail('')
			setMessage('')
			setTimeout(() => setEmailSentTo(null), 4000)
		} catch (e: any) {
			setError(e?.message || 'Could not send email')
		} finally {
			setSending(false)
		}
	}, [documentId, versionId, ttlDays, email, message, createLink])

	const handleRevoke = useCallback(async () => {
		if (!link?.token || !documentId) return
		const ok = await toast.confirm({
			title: 'Revoke share link',
			message: 'Revoke this share link? Anyone who has the URL will lose access immediately.',
			confirmLabel: 'Revoke',
		})
		if (!ok) return
		setRevoking(true)
		setError(null)
		try {
			await revokeLink({ token: link.token, documentId })
			onClose()
		} catch (e: any) {
			setError(e?.message || 'Could not revoke link')
			setRevoking(false)
		}
	}, [link, documentId, revokeLink, onClose])

	const expiresAtLabel = link?.expiresAt
		? moment(link.expiresAt).format('MMM D, YYYY [at] h:mm A')
		: ''

	const sendDisabled =
		sending ||
		!email.trim() ||
		!isProbablyEmail(email.trim()) ||
		!documentId ||
		creating

	return (
		<Dialog open={open} onClose={onClose}>
			<DialogContent className="max-w-lg">
				<DialogHeader>
					<div className="flex items-center gap-3">
						<div
							className="w-10 h-10 rounded-lg flex items-center justify-center text-white font-semibold text-[11px] flex-shrink-0"
							style={{ backgroundColor: fileIcon.color }}
						>
							{fileIcon.label}
						</div>
						<div>
							<DialogTitle>Share document</DialogTitle>
							<DialogDescription>
								Anyone with the link can download. Links expire after the
								selected duration.
							</DialogDescription>
						</div>
					</div>
				</DialogHeader>

				<div className="px-5 py-4 flex flex-col gap-5">
					{/* Copy link section */}
					<div className="flex flex-col gap-2">
						<div className="flex items-end justify-between gap-3">
							<Label className="text-xs text-gray-700">Shareable link</Label>
							<div className="flex items-center gap-2">
								<Label className="text-xs text-gray-500">Expires in</Label>
								<Select
									value={String(ttlDays)}
									onChange={handleTtlChange}
									disabled={creating}
									className="h-8 w-[110px] text-xs"
								>
									{TTL_OPTIONS.map(opt => (
										<option key={opt.value} value={opt.value}>
											{opt.label}
										</option>
									))}
								</Select>
							</div>
						</div>
						<div className="flex items-center gap-2">
							<Input
								readOnly
								value={creating ? 'Creating link…' : link?.url ?? ''}
								className="font-mono text-xs"
								onClick={e => (e.target as HTMLInputElement).select()}
							/>
							<Button
								type="button"
								onClick={handleCopy}
								disabled={!link || creating || revoking}
								variant={copied ? 'success' : 'primary'}
								className="gap-1 whitespace-nowrap"
							>
								{copied ? (
									<>
										<Check className="w-4 h-4" />
										Copied
									</>
								) : (
									<>
										<Copy className="w-4 h-4" />
										Copy
									</>
								)}
							</Button>
						</div>
						{expiresAtLabel && !creating && (
							<div className="text-xs text-gray-500">
								Expires {expiresAtLabel}
							</div>
						)}
					</div>

					<div className="border-t border-gray-200" />

					{/* Email section */}
					<div className="flex flex-col gap-2">
						<Label className="text-xs text-gray-700">
							Email a link to someone
						</Label>
						<Input
							type="email"
							placeholder="recipient@example.com"
							value={email}
							onChange={e => setEmail(e.target.value)}
							disabled={sending}
						/>
						<Textarea
							placeholder="Optional message…"
							rows={2}
							value={message}
							onChange={e => setMessage(e.target.value)}
							disabled={sending}
						/>
						<div className="flex items-center justify-between">
							{emailSentTo ? (
								<span className="text-xs text-green-700">
									Sent to {emailSentTo}.
								</span>
							) : (
								<span className="text-xs text-gray-500">
									We'll send a separate link with the same expiry.
								</span>
							)}
							<Button
								type="button"
								onClick={handleSendEmail}
								disabled={sendDisabled}
								className="gap-1"
							>
								{sending ? (
									<Loader2 className="w-4 h-4 animate-spin" />
								) : (
									<Mail className="w-4 h-4" />
								)}
								{sending ? 'Sending…' : 'Send'}
							</Button>
						</div>
					</div>

					{error && (
						<div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded p-2">
							{error}
						</div>
					)}
				</div>

				<DialogFooter>
					<Button
						type="button"
						variant="ghost-red"
						onClick={handleRevoke}
						disabled={!link || revoking || creating}
						className="gap-1 mr-auto"
					>
						<Trash2 className="w-4 h-4" />
						{revoking ? 'Revoking…' : 'Revoke link'}
					</Button>
					<Button type="button" variant="ghost" onClick={onClose}>
						Done
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	)
}

export default DocumentShareDialog
